import * as SelectPrimitive from '@radix-ui/react-select';
import { LuCheck, LuChevronDown } from 'react-icons/lu';
import { css, cva } from '../../styled-system/css';
import type { SystemStyleObject } from '../../styled-system/types';
import { forwardRef } from 'react';
import { Label } from './Label';

const triggerRecipe = cva({
    base: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderWidth: '1px',
        borderColor: 'main.border.1',
        bg: 'main.background.1',
        color: 'main.text.1',
        rounded: 'md!',
        px: '3',
        gap: '2',
        cursor: 'pointer',
        transition: 'all 0.2s',
        width: '100%',

        _hover: {
            borderColor: 'main.border.2',
        },
        _focus: {
            outline: 'none',
            borderColor: 'main.solid.1',
        },
        _disabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
    },
    variants: {
        size: {
            sm: { height: '10', fontSize: 'sm' },
            md: { height: '12', fontSize: 'md' },
            lg: { height: '14', fontSize: 'lg' },
        },
        visual: {
            default: { bg: 'main.background.1' },
            filled: { bg: 'main.background.2' },
            ghost: { bg: 'transparent' },
        },
    },
    defaultVariants: {
        size: 'md',
        visual: 'default',
    },
});

const contentRecipe = cva({
    base: {
        overflow: 'hidden',
        bg: 'main.background.1',
        rounded: 'md',
        borderWidth: '1px',
        borderColor: 'main.border.1',
        zIndex: '50',
        boxShadow: 'lg',
        minWidth: 'var(--radix-select-trigger-width)',
        maxHeight: 'var(--radix-select-content-available-height)',
    },
});

const itemRecipe = cva({
    base: {
        fontSize: 'sm',
        rounded: 'sm',
        display: 'flex',
        alignItems: 'center',
        py: '1.5',
        px: '3',
        position: 'relative',
        userSelect: 'none',
        color: 'main.text.1',
        cursor: 'pointer',

        _focus: {
            bg: 'main.background.2',
            outline: 'none',
        },

        '&[data-disabled]': {
            color: 'main.text.3',
            pointerEvents: 'none',
        },

        '&[data-highlighted]': {
            bg: 'main.background.2',
            color: 'main.text.1',
        },
    },
});

export type SelectOption = {
    value: string;
    label: string;
};

type SelectProps = {
    options: SelectOption[];
    value?: string;
    onChange?: (value: string) => void;
    placeholder?: string;
    disabled?: boolean;
    css?: SystemStyleObject;
    size?: 'sm' | 'md' | 'lg';
    visual?: 'default' | 'filled' | 'ghost';
    label?: string;
};

export const Select = forwardRef<HTMLButtonElement, SelectProps>(
    ({
        options,
        value,
        onChange,
        placeholder,
        disabled = false,
        css: cssProp,
        size,
        visual,
        label
    }, ref) => {
        return (
            <div className={css({ display: 'flex', flexDirection: 'column', gap: '2' })}>
                {label && (
                    <Label>
                        {label}
                    </Label>
                )}
                <SelectPrimitive.Root value={value} onValueChange={onChange} disabled={disabled}>
                    <SelectPrimitive.Trigger
                        ref={ref}
                        className={css(triggerRecipe.raw({ size, visual }), cssProp)}
                    >
                        <SelectPrimitive.Value placeholder={placeholder} />
                        <SelectPrimitive.Icon>
                            <LuChevronDown />
                        </SelectPrimitive.Icon>
                    </SelectPrimitive.Trigger>

                    <SelectPrimitive.Portal>
                        <SelectPrimitive.Content className={contentRecipe()}>
                            <SelectPrimitive.Viewport>
                                {options.map((option) => (
                                    <SelectPrimitive.Item
                                        key={option.value}
                                        value={option.value}
                                        className={itemRecipe()}
                                    >
                                        <SelectPrimitive.ItemText>{option.label}</SelectPrimitive.ItemText>
                                        <SelectPrimitive.ItemIndicator className={css({
                                            position: 'absolute',
                                            right: '2',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                        })}>
                                            <LuCheck />
                                        </SelectPrimitive.ItemIndicator>
                                    </SelectPrimitive.Item>
                                ))}
                            </SelectPrimitive.Viewport>
                        </SelectPrimitive.Content>
                    </SelectPrimitive.Portal>
                </SelectPrimitive.Root>
            </div>
        );
    }
);

Select.displayName = 'Select'; 